// @ts-strict-ignore
import { component } from "picoapp";
import {
  IMAGE_LOAD_FILTERS,
  MAIN_PRODUCT_CLASS,
  SUPPLEMENTAL_PRODUCT_CLASS,
} from "../../constants";
import {
  type Context,
  type ImageMedia,
  type SelectedVariant,
} from "../../state";
import {
  updateMaestroVariants,
  updatePictureElement,
  updateVideoElement,
} from "../../utilities";

const PRODUCT_GALLERY_VIDEO_TYPES = ["video/mp4", "video/webm"];
const PRODUCT_GALLERY_GRID = {
  ID: "#product-gallery-grid",
  IMAGE_SELECTOR: `:scope > div > div > picture:not(.hidden):not([class*="-kit-image-"])`,
  VIDEO_SELECTOR: ":scope > div > div > video:not(.hidden)",
  VIDEO_SIZE: 374,
};
const PRODUCT_GALLERY_CAROUSEL = {
  ID: "#product-gallery-carousel",
  IMAGE_SELECTOR: `picture:not(.hidden):not([class*="-kit-image-"])`,
  VIDEO_SELECTOR: "video:not(.hidden)",
  VIDEO_SIZE: 767,
};

const generateVideoElement = ({
  ariaLabel,
  height,
  imageUrl,
  type,
  width,
}: {
  ariaLabel: string;
  height: number;
  imageUrl: string;
  type: string;
  width: number;
}) => {
  const sourceEl = document.createElement("source");
  sourceEl.type = type;
  sourceEl.src = "";

  const imageEl = document.createElement("img");
  imageEl.src = imageUrl;

  const videoEl = document.createElement("video");
  videoEl.ariaLabel = ariaLabel;
  videoEl.autoplay = true;
  videoEl.loop = true;
  videoEl.muted = true;
  videoEl.playsInline = true;
  videoEl.poster = imageUrl;
  videoEl.preload = "none";
  videoEl.height = height;
  videoEl.width = width;
  videoEl.classList.add("block", "w-full", "h-full", "object-cover");

  videoEl.appendChild(sourceEl);
  videoEl.appendChild(imageEl);
  return videoEl;
};

const updateProductGallery = ({
  gallery,
  imageSelector,
  variant,
  videoSelector,
  videoSize,
}: {
  gallery: HTMLElement;
  imageSelector: string;
  variant: SelectedVariant;
  videoSelector: string;
  videoSize: number;
}) => {
  const kitElements = gallery.querySelectorAll<HTMLPictureElement>(
    `picture[class*="-kit-image-"]`
  );
  const mediaElements = gallery.querySelectorAll<
    HTMLPictureElement | HTMLVideoElement
  >(`${imageSelector}, ${videoSelector}`);

  Array.from(mediaElements)
    .filter(
      (element: HTMLPictureElement | HTMLVideoElement) =>
        !element.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS)
    )
    .forEach((element, idx) => {
      const currentSource = kitElements.length
        ? variant.media[idx + (mediaElements.length - kitElements.length)]
        : variant.media[idx];

      if (!currentSource) return;

      const isElementCompatible =
        currentSource.contentType === "IMAGE"
          ? element.nodeName === "PICTURE"
          : element.nodeName === currentSource.contentType;
      let targetEl;

      switch (currentSource.contentType) {
        case "IMAGE":
          targetEl = isElementCompatible
            ? element
            : gallery
                .querySelector<HTMLPictureElement>(imageSelector)
                .cloneNode(true);

          if (targetEl) {
            updatePictureElement({
              alt: currentSource.alt || null,
              element: targetEl as HTMLPictureElement,
              source:
                idx === 0 && variant.alternateImage
                  ? variant.alternateImage.image
                  : currentSource.image,
            });
          }

          break;
        case "VIDEO":
          targetEl = isElementCompatible
            ? element
            : generateVideoElement({
                ariaLabel: variant.productTitle,
                height: videoSize,
                imageUrl: currentSource.previewImage.url,
                type: currentSource.sources.find((source) =>
                  PRODUCT_GALLERY_VIDEO_TYPES.includes(source.mimeType)
                )?.mimeType,
                width: videoSize,
              });

          if (targetEl) {
            updateVideoElement({
              element: targetEl as HTMLVideoElement,
              sources: currentSource.sources,
            });
          }

          break;
      }

      if (!isElementCompatible) {
        element.replaceWith(targetEl);
      }
    });
};

const updateProductVariantMedia = ({
  carousel,
  grid,
  variant,
}: {
  carousel: HTMLElement;
  grid: HTMLElement;
  variant: SelectedVariant;
}) => {
  updateProductGallery({
    gallery: grid,
    imageSelector: PRODUCT_GALLERY_GRID.IMAGE_SELECTOR,
    variant,
    videoSelector: PRODUCT_GALLERY_GRID.VIDEO_SELECTOR,
    videoSize: PRODUCT_GALLERY_GRID.VIDEO_SIZE,
  });

  updateProductGallery({
    gallery: carousel,
    imageSelector: PRODUCT_GALLERY_CAROUSEL.IMAGE_SELECTOR,
    variant,
    videoSelector: PRODUCT_GALLERY_CAROUSEL.VIDEO_SELECTOR,
    videoSize: PRODUCT_GALLERY_CAROUSEL.VIDEO_SIZE,
  });

  const thumbnailImageElement = document.querySelector<HTMLPictureElement>(
    `fieldset[data-product-handle="${variant.handle}"] picture`
  );

  if (thumbnailImageElement) {
    updatePictureElement({
      element: thumbnailImageElement,
      source: variant.media.find(
        (media): media is ImageMedia => media.contentType === "IMAGE"
      ).image,
    });
  }
};

const updateProductVariantBadgeImage = ({
  grid,
  variant,
}: {
  grid: HTMLElement;
  variant: SelectedVariant;
}) => {
  const variantBadgeImageElement = grid.querySelector<HTMLPictureElement>(
    ".pdp-variant-badge-image"
  );
  const awardBadgeImageElement = grid.querySelector<HTMLPictureElement>(
    ".pdp-award-badge-image"
  );
  const variantBadgeImage = variant.badgeImage;

  if (variantBadgeImage && variantBadgeImageElement) {
    updatePictureElement({
      element: variantBadgeImageElement,
      source: variantBadgeImage.image,
    });
    awardBadgeImageElement?.classList.add("hidden");
    variantBadgeImageElement?.classList.remove("hidden");
  } else if (awardBadgeImageElement) {
    awardBadgeImageElement?.classList.remove("hidden");
    variantBadgeImageElement?.classList.add("hidden");
  } else {
    awardBadgeImageElement?.classList.add("hidden");
    variantBadgeImageElement?.classList.add("hidden");
  }
};

const updateMainProductVariantMedia = ({
  carousel,
  grid,
  variant,
}: {
  carousel: HTMLElement;
  grid: HTMLElement;
  variant: SelectedVariant;
}) => {
  const gridMainProductImageElement = grid.querySelector<HTMLPictureElement>(
    `picture.${MAIN_PRODUCT_CLASS}`
  );

  const carouselMainProductImageElement =
    carousel.querySelector<HTMLPictureElement>(`picture.${MAIN_PRODUCT_CLASS}`);

  const mainThumbnailImageElement = document.querySelector<HTMLPictureElement>(
    `fieldset[data-supplemental-product="false"][data-product-handle="${variant.handle}"] picture`
  );

  const { image } = variant.media.find(
    (media): media is ImageMedia => media.contentType === "IMAGE"
  );

  [
    gridMainProductImageElement,
    carouselMainProductImageElement,
    mainThumbnailImageElement,
  ]
    .filter(
      (element): element is HTMLPictureElement =>
        element instanceof HTMLPictureElement
    )
    .forEach((element) => {
      updatePictureElement({
        element,
        source: image,
      });
    });
};

const updateSupplementalProductVariantMedia = ({
  carousel,
  grid,
  variant,
}: {
  carousel: HTMLElement;
  grid: HTMLElement;
  variant: SelectedVariant;
}) => {
  const gridSupplementalProductImageElement = grid.querySelector<
    HTMLPictureElement | undefined
  >(`picture.${SUPPLEMENTAL_PRODUCT_CLASS}`);

  const carouselSupplementalProductImageElement = carousel.querySelector<
    HTMLPictureElement | undefined
  >(`picture.${SUPPLEMENTAL_PRODUCT_CLASS}`);

  const supplementalThumbnailImageElement = document.querySelector<
    HTMLPictureElement | undefined
  >(
    `fieldset[data-supplemental-product="true"][data-product-handle="${variant.handle}"] picture`
  );

  const { image } = variant.media.find(
    (media): media is ImageMedia => media.contentType === "IMAGE"
  );

  [
    gridSupplementalProductImageElement,
    carouselSupplementalProductImageElement,
    supplementalThumbnailImageElement,
  ]
    .filter(
      (element): element is HTMLPictureElement =>
        element instanceof HTMLPictureElement
    )
    .forEach((element) => {
      updatePictureElement({
        element,
        source: image,
      });
    });
};

const showCompositeLayout = ({
  carousel,
  grid,
}: {
  carousel: HTMLElement;
  grid: HTMLElement;
}) => {
  [carousel, grid].forEach((element) => {
    element
      .querySelectorAll<HTMLPictureElement>(`picture[class*="-kit-image-"]`)
      .forEach((media) => media.classList.add("hidden"));

    element
      .querySelector<HTMLPictureElement>(`picture.${MAIN_PRODUCT_CLASS}`)
      ?.classList.remove("hidden");
    element
      .querySelector<HTMLPictureElement>(
        `picture.${SUPPLEMENTAL_PRODUCT_CLASS}`
      )
      ?.classList.remove("hidden");
  });
};

const showMergedLayout = ({
  carousel,
  grid,
  node,
  variant,
}: {
  carousel: HTMLElement;
  grid: HTMLElement;
  node: HTMLElement;
  variant: SelectedVariant;
}) => {
  const mediaLayouts = [
    {
      element: grid,
      selector: `.hidden-media picture.grid-kit-image-${variant.id}`,
      variantId: variant.id,
    },
    {
      element: carousel,
      selector: `.hidden-media picture.carousel-kit-image-${variant.id}`,
      variantId: variant.id,
    },
  ];

  mediaLayouts.forEach(({ element, selector, variantId }) => {
    const hiddenMedia = node.querySelector<HTMLPictureElement>(selector);

    if (hiddenMedia) {
      const hiddenMediaClone = hiddenMedia.cloneNode(
        true
      ) as HTMLPictureElement;

      element
        .querySelector<HTMLPictureElement>(`picture.${MAIN_PRODUCT_CLASS}`)
        ?.before(hiddenMediaClone);

      hiddenMedia.remove();
    }

    element
      .querySelector<HTMLPictureElement>(
        `picture[class*="-kit-image-${variantId}"]`
      )
      ?.classList.remove("hidden");
    element
      .querySelectorAll<HTMLPictureElement>(
        `picture[class*="-kit-image"]:not([class*="${variantId}"])`
      )
      .forEach((media) => media.classList.add("hidden"));
    element
      .querySelector<HTMLPictureElement>(`picture.${MAIN_PRODUCT_CLASS}`)
      ?.classList.add("hidden");
    element
      .querySelector<HTMLPictureElement>(
        `picture.${SUPPLEMENTAL_PRODUCT_CLASS}`
      )
      ?.classList.add("hidden");
  });
};

export const productHero = component<Context>((node, context) => {
  const existingGrid = node.querySelector<HTMLElement>(PRODUCT_GALLERY_GRID.ID);
  const existingCarousel = node.querySelector<HTMLElement>(
    PRODUCT_GALLERY_CAROUSEL.ID
  );

  const existingMedia = Array.from(
    existingGrid.querySelectorAll<HTMLPictureElement | HTMLVideoElement>(
      "picture, video"
    )
  ).concat(
    Array.from(
      existingCarousel.querySelectorAll<HTMLPictureElement | HTMLVideoElement>(
        "picture, video"
      )
    )
  );

  const updateProductVariantBadge = (selectedVariant) => {
    const variantBadges = node.querySelectorAll<HTMLElement>(
      ".pdp-variant-badge [data-tbsp-component='badge']"
    );

    if (!variantBadges.length) return;

    let { badgeText, badgeType } = selectedVariant;

    const isProductBundle = node.dataset.productBundle === "true";

    if (isProductBundle) {
      const checkedInputs = node.querySelectorAll(
        "#product-form input[type='radio'][checked]"
      );
      const isKitted =
        checkedInputs.length === 1 &&
        !!checkedInputs[0].closest("[data-kitted-product=true");

      if (!isKitted) {
        badgeText = node.dataset.productBundleBadgeText;
        badgeType = node.dataset.productBundleBadgeType;
      }
    }

    variantBadges.forEach((variantBadge) => {
      if (badgeText) {
        let newBackgroundClassName;
        switch (badgeType) {
          case "sage":
            newBackgroundClassName = "bg-sage-700";
            break;
          case "brick":
            newBackgroundClassName = "bg-interactive-brick";
            break;
          case "bright-blue":
            newBackgroundClassName = "bg-misc-bright-blue";
            break;
          default:
            newBackgroundClassName = "bg-interactive-char";
        }

        const backgroundColorClassNames = [...variantBadge.classList].filter(
          (className) => className.startsWith("bg-")
        );

        variantBadge.innerHTML = badgeText;
        variantBadge.classList.remove(...backgroundColorClassNames);
        variantBadge.classList.add(newBackgroundClassName);
        variantBadge.classList.remove("hidden");
      } else {
        variantBadge.innerHTML = "";
        variantBadge.classList.add("hidden");
      }
    });
  };

  context.on(
    "tbsp:variant-metadata-loading",
    ({ fieldset, isMainProduct, isSupplementalProduct }) => {
      const supplementalMedia = existingMedia.filter((media) =>
        media.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS)
      );
      const mainMedia = existingMedia.filter((media) =>
        media.classList.contains(MAIN_PRODUCT_CLASS)
      );
      const media = existingMedia.filter(
        (media) =>
          !media.classList.contains(MAIN_PRODUCT_CLASS) &&
          media.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS)
      );

      supplementalMedia.forEach((media) => {
        if (
          (isSupplementalProduct &&
            media.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS)) ||
          (!isSupplementalProduct &&
            !media.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS))
        ) {
          media.classList.add("transition-all", ...IMAGE_LOAD_FILTERS);
        }
      });

      mainMedia.forEach((media) => {
        if (
          (isMainProduct && media.classList.contains(MAIN_PRODUCT_CLASS)) ||
          (!isMainProduct && !media.classList.contains(MAIN_PRODUCT_CLASS))
        ) {
          media.classList.add("transition-all", ...IMAGE_LOAD_FILTERS);
        }
      });

      media.forEach((media) => {
        if (
          (isSupplementalProduct &&
            media.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS)) ||
          (!isSupplementalProduct &&
            !media.classList.contains(SUPPLEMENTAL_PRODUCT_CLASS))
        ) {
          media.classList.add("transition-all", ...IMAGE_LOAD_FILTERS);
        } else if (
          (isMainProduct && media.classList.contains(MAIN_PRODUCT_CLASS)) ||
          (!isMainProduct && !media.classList.contains(MAIN_PRODUCT_CLASS))
        ) {
          media.classList.add("transition-all", ...IMAGE_LOAD_FILTERS);
        }
      });
      fieldset.querySelector("picture")?.classList.add(...IMAGE_LOAD_FILTERS);
    }
  );

  context.on("tbsp:variant-selected", async ({ selectedVariant }) => {
    const mediaParams = {
      grid: existingGrid,
      carousel: existingCarousel,
      variant: selectedVariant,
    };

    if (selectedVariant.mainProduct || selectedVariant.supplementalProduct) {
      showCompositeLayout(mediaParams);
    }

    if (selectedVariant.mainProduct) {
      updateMainProductVariantMedia(mediaParams);
    } else if (selectedVariant.supplementalProduct) {
      updateSupplementalProductVariantMedia(mediaParams);
    } else {
      showMergedLayout({ ...mediaParams, node });

      // update metrics
      updateMaestroVariants(node, selectedVariant);

      updateProductVariantMedia(mediaParams);
      updateProductVariantBadgeImage(mediaParams);
    }

    updateProductVariantBadge(selectedVariant);
  });
});
